/**
 * The case journey Lloyd drew in "Automation steps August 2026.docx" (Aug 18 2026), plus the five
 * steps Kelsi described when he asked her to break down what she actually does (Aug 18, evening).
 *
 * Kelsi's are the ones that start when a date lands on the calendar: tell the client, get the
 * prosecutor's details, ask for an offer, pass the offer on, and — when no offer comes — arrange
 * coverage or file a motion for continuance. Four of the five sit between "court date set" and
 * "offer received", which was previously a single hop and is where cases actually go quiet.
 *
 * The eight-stage bar that shipped in July is the spine of this, and it stays exactly as it was —
 * CASE_STAGES, stageForStatus, CLOSED_STAGE_INDEX and the open/closed filters that depend on them
 * are untouched. What this adds is the rest of the map: the offer breakdown from Dani's email, the
 * appeal-and-bond path, the jury-trial path, and the second case that opens at county level.
 *
 * ## Rows are phases, not branches (Lloyd, Aug 19)
 *
 * These thirty-nine steps first shipped grouped by branch — "every case", "if the client accepts",
 * "if rejected — appeal" — which is how the process forks but not how anybody reads a chart. Lloyd:
 * *"Perhaps we should do 4 rows equally divided? One for everything to do with LOR; a second for
 * calendar; a 3rd for offers, and a 4th for closure ... such a view would allow the staff to see
 * instantly where a case is."*
 *
 * So the four phase rows are his, and every case has all four. What his four do not cover is the
 * other twenty: appeal, jury trial and the second county case are not phases every case passes
 * through, they are **alternatives to each other**. Folding them into "closure" would produce one
 * row of twenty-six boxes reading as a sequence, which is worse than the problem it set out to fix.
 * They therefore keep their own rows, marked `kind: 'outcome'`, and the bar keeps them closed until
 * asked for.
 *
 * **Nothing records which path a case took**, so the bar cannot open the right one by itself — see
 * the disclosure in CaseStatusTimeline, which says so rather than guessing.
 *
 * ## The `tracked` flag is the point of this file
 *
 * Lloyd asked to "capture as many decision making points as possible, and then automate where
 * possible". Drawing thirty-nine boxes is easy; the useful question is which of them the system
 * can actually know about today. Ten can. The rest have no field, no event and no trigger behind
 * them — nothing would ever light them up.
 *
 * So each step declares whether it is tracked, and the bar draws untracked steps hollow. That turns
 * the chart into the worklist for the automation Lloyd is asking for: every hollow box is a
 * decision point that needs data capture before it can be automated. Drawing them solid but
 * permanently grey would have looked identical to "nothing has happened on this case yet", which is
 * the one reading that would mislead him.
 *
 * `canonicalStage` ties a tracked step back to its index in CASE_STAGES, which decides whether it
 * shows as done, current or future, and what a click writes to TicketStatus. Those indices are
 * global, not per row, so a case at "closed" correctly shows the LOR and offer rows behind it
 * filled in.
 *
 * Step `id`s are stable and were not touched by the regrouping — only which row each one sits on.
 */

export type JourneyTrack =
  | 'lor'
  | 'calendar'
  | 'offers'
  | 'closure'
  | 'appeal'
  | 'trial'
  | 'county';

/**
 * Facts a case can carry that fill in a step without being one of the eight canonical stages.
 *
 * Two so far, both from the discovery record Lloyd asked for on Aug 19. Every one added here should
 * come with the field that sets it — a key with nothing writing to it is a solid box that never
 * fills, which is the exact confusion the hollow-box style exists to prevent.
 */
export type JourneyFactKey =
  | 'offerRequested'
  | 'acceptanceSentToCourt'
  | 'paperworkSigned'
  | 'trialDateSet'
  | 'bondEmailed'
  | 'bondPosted'
  | 'discoveryRequested'
  | 'discoveryReceived'
  | 'offerConveyed'
  | 'offerRejected'
  | 'juryTrialRequested'
  | 'conditionDueDatesSet'
  | 'bondCreated'
  | 'appealBondCreated';

/** What the bar knows about a case beyond its stage. Absent keys read as "not yet". */
export type JourneyFacts = Partial<Record<JourneyFactKey, boolean>>;

export interface JourneyStep {
  /** Stable identifier. Never reuse one — codes, labels and rows are free to change, this is not. */
  id: string;
  /** The abbreviation shown in the bar. Lloyd, Aug 18: "LOR Sent ... could be LS". */
  code: string;
  /** What the abbreviation means. Shown on hover and in the expanded key. */
  label: string;
  track: JourneyTrack;
  /**
   * Does the system know when this happens? False means the box is drawn but can never fill in
   * until something records it. Be honest here — an optimistic `true` produces a stage that
   * silently never advances, which is worse than an obviously empty one.
   */
  tracked: boolean;
  /** Index into CASE_STAGES, when this is one of the eight the system already follows. */
  canonicalStage?: number;
  /**
   * A fact recorded on the case that fills this box in, for steps that are tracked but are not one
   * of the eight canonical stages.
   *
   * The eight stages are a position on a line: a case is at exactly one of them, and moving to the
   * next leaves the previous behind. Discovery is not like that — a request can go out and come
   * back while the case sits at "pre-trial", and neither event moves the case along the spine. So
   * these steps read a boolean off the case rather than a stage index, and clicking them does
   * nothing: they are filled in on the Discovery tab, where the dates are.
   */
  factKey?: JourneyFactKey;
  /** What it would take to track it. Shown on hover for hollow steps. */
  note?: string;
}

export interface JourneyBranch {
  track: JourneyTrack;
  /** Row heading in the chart. */
  title: string;
  /**
   * A phase every case passes through, or one of the mutually exclusive paths a case may take
   * after an offer. Outcome rows stay closed until someone asks for them.
   */
  kind: 'phase' | 'outcome';
  /** Shown above the outcome rows when they are opened. */
  note?: string;
  steps: JourneyStep[];
}

/** Phase 1 — getting the firm on the record. Lloyd: "everything to do with LOR". */
const LOR: JourneyStep[] = [
  { id: 'PENDING', code: 'P', label: 'Pending payment', track: 'lor', tracked: true, canonicalStage: 0 },
  { id: 'LOR_SENT', code: 'LS', label: 'LOR sent to the court', track: 'lor', tracked: true, canonicalStage: 1 },
  { id: 'LOR_RECEIVED', code: 'LR', label: 'LOR received / confirmed by the court', track: 'lor', tracked: true, canonicalStage: 2 },
];

/**
 * Phase 2 — the setting, and everything that has to happen because of it.
 *
 * Kelsi's steps 1, 2 and 5 live here: telling the client, getting hold of the prosecutor, and — when
 * no offer has arrived and the setting is coming anyway — arranging coverage or filing a motion for
 * continuance. Those last two used to sit on their own "if no offer comes back" row; they are
 * calendar work, and they belong beside the date that forces them.
 */
const CALENDAR: JourneyStep[] = [
  { id: 'COURT_DATE', code: 'CD', label: 'Court date set', track: 'calendar', tracked: true, canonicalStage: 3 },
  {
    id: 'CLIENT_TOLD_DATE',
    code: 'DN',
    label: 'Client told about the court date',
    track: 'calendar',
    tracked: false,
    note: 'Kelsi, step 1. The system can already email and text this when a notice says the client must attend, but that send is switched off and, more to the point, nothing writes it back to the case — so there is no record of who was told or when.',
  },
  {
    id: 'PROSECUTOR_CONTACT',
    code: 'PC',
    label: 'Prosecutor contact obtained (from the court, or direct)',
    track: 'calendar',
    tracked: false,
    note: 'Kelsi, step 2 — which of the two routes depends on where the case came from. A prosecutor can already be linked to a case (Prosecutor_id), so this is the cheapest of these to start tracking.',
  },
  {
    id: 'COVERAGE',
    code: 'CV',
    label: 'Coverage arranged for the setting',
    track: 'calendar',
    tracked: false,
    note: 'Kelsi, step 5, when no offer has come back and the setting is coming anyway. No concept of coverage exists in the system at all.',
  },
  {
    // Lloyd, Aug 19: "40 if we can add the motion to reset / continue tab". This step already
    // existed as Kelsi's step 5 — it is relabelled in his words rather than added a second time,
    // because a chart with both "motion for continuance" and "motion to reset" on it would imply
    // the firm files two different things. **Open question for him**: in his practice a reset and a
    // continuance may well be distinct — an administrative reschedule versus a filed motion — and
    // if they are, this splits into two steps and the tab records which. Not guessed at here.
    id: 'MOC_FILED',
    code: 'MOC',
    label: 'Motion to reset / continuance filed',
    track: 'calendar',
    tracked: false,
    note: 'Kelsi, step 5. Nothing records it — not as a document, a status, or a court event type. The tab that would fill this in is the same shape as the Discovery tab and is not built.',
  },
];

/**
 * Phase 3 — the offer, from asking for it to the client answering.
 *
 * Both answers sit on this row on purpose. Accepting and rejecting are the same moment in the
 * process, and the two things that follow from them are what the outcome rows below are for.
 */
const OFFERS: JourneyStep[] = [
  {
    id: 'OFFER_REQUESTED',
    code: 'OQ',
    label: 'Offer requested from the prosecutor',
    track: 'offers',
    tracked: true,
    factKey: 'offerRequested',
  },
  { id: 'OFFER_IN', code: 'OF', label: 'Offer received from the court', track: 'offers', tracked: true, canonicalStage: 4 },
  {
    id: 'OFFER_CONVEYED',
    code: 'OC',
    label: 'Offer conveyed to the client, by email and text',
    track: 'offers',
    tracked: true,
    factKey: 'offerConveyed',
  },
  { id: 'OFFER_ACCEPTED', code: 'OA', label: 'Offer accepted by the client', track: 'offers', tracked: true, canonicalStage: 5 },
  {
    id: 'OFFER_REJECTED',
    code: 'RJ',
    label: 'Offer rejected by the client',
    track: 'offers',
    tracked: true,
    factKey: 'offerRejected',
  },
];

/** Phase 4 — everything between an accepted offer and a closed file. Dani's breakdown. */
const CLOSURE: JourneyStep[] = [
  {
    id: 'ACCEPT_TO_COURT',
    code: 'AS',
    label: 'Acceptance sent to the court',
    track: 'closure',
    tracked: true,
    factKey: 'acceptanceSentToCourt',
  },
  {
    id: 'PAPERWORK_IN',
    code: 'PR',
    label: 'Paperwork received from the court',
    track: 'closure',
    tracked: false,
    note: 'Not recorded yet. Would come from the court mailbox once email-to-case is switched on.',
  },
  {
    id: 'PAPERWORK_SIGNED',
    code: 'PS',
    label: 'Paperwork signed',
    track: 'closure',
    tracked: true,
    factKey: 'paperworkSigned',
  },
  { id: 'CONDITIONS', code: 'CN', label: 'Deferral / DSC conditions (affidavit, driving record)', track: 'closure', tracked: true, canonicalStage: 6 },
  {
    id: 'DUE_DATES',
    code: 'DD',
    label: 'Condition due dates on the calendar',
    track: 'closure',
    tracked: true,
    factKey: 'conditionDueDatesSet',
  },
  { id: 'CLOSED', code: 'CL', label: 'Case closed', track: 'closure', tracked: true, canonicalStage: 7 },
];

/** Outcome — the client rejects and we appeal to county. Ends by opening a SECOND case. */
const APPEAL: JourneyStep[] = [
  { id: 'APPEAL', code: 'AP', label: 'Appeal to county', track: 'appeal', tracked: true, factKey: 'appealBondCreated' },
  {
    id: 'APPEAL_PAID',
    code: 'PD',
    label: 'Appeal fee paid',
    track: 'appeal',
    tracked: false,
    note: 'Not recorded yet. Payments exist, but not this fee specifically.',
  },
  { id: 'BOND_CREATED', code: 'BC', label: 'Bond created', track: 'appeal', tracked: true, factKey: 'bondCreated' },
  {
    id: 'BOND_EMAILED',
    code: 'BE',
    label: 'Bond sent to the court by email',
    track: 'appeal',
    tracked: true,
    factKey: 'bondEmailed',
  },
  {
    id: 'BOND_POSTED',
    code: 'BM',
    label: 'Bond sent to the court by post',
    track: 'appeal',
    tracked: true,
    factKey: 'bondPosted',
  },
  {
    id: 'COUNTY_CASE',
    code: 'NC',
    label: 'New case opened at county',
    track: 'appeal',
    tracked: false,
    note: 'Not built. An appeal opens a second case; today a case carries one timeline.',
  },
];

/** Outcome — the client rejects and we go to trial, from the original court. */
const TRIAL: JourneyStep[] = [
  { id: 'JURY_REQUESTED', code: 'JT', label: 'Jury trial requested', track: 'trial', tracked: true, factKey: 'juryTrialRequested' },
  {
    id: 'TRIAL_DATE',
    code: 'TD',
    label: 'Trial date set',
    track: 'trial',
    tracked: true,
    factKey: 'trialDateSet',
  },
  {
    id: 'DISCOVERY_SENT',
    code: 'DS',
    label: 'Discovery request sent',
    track: 'trial',
    tracked: true,
    factKey: 'discoveryRequested',
    note: 'Recorded on the Discovery tab. The 39.14 request generates with the not-guilty letter, but somebody still has to say it went.',
  },
  {
    id: 'DISCOVERY_IN',
    code: 'DR',
    label: 'Discovery received',
    track: 'trial',
    tracked: true,
    factKey: 'discoveryReceived',
    note: 'Recorded on the Discovery tab, along with where the material physically is.',
  },
  { id: 'CLIENT_NOTIFIED', code: 'CN2', label: 'Client notified of the trial', track: 'trial', tracked: false, note: 'Not recorded yet.' },
  { id: 'TRIAL_HELD', code: 'JTH', label: 'Jury trial held', track: 'trial', tracked: false, note: 'Not recorded yet.' },
];

/** Outcome — the second case, at county. Mirrors the four phases and forks the same way. */
const COUNTY: JourneyStep[] = [
  {
    id: 'C_APPEALED',
    code: 'AC',
    label: 'Appealed to county',
    track: 'county',
    tracked: false,
    note: 'Belongs to the second case, which does not exist yet.',
  },
  { id: 'C_CONFIRMED', code: 'CF', label: 'County confirmation received', track: 'county', tracked: false },
  { id: 'C_COURT_DATE', code: 'CD2', label: 'County court date', track: 'county', tracked: false },
  { id: 'C_OFFER', code: 'OF2', label: 'County offer', track: 'county', tracked: false },
  { id: 'C_CONVEYED', code: 'OC2', label: 'Conveyed to the client', track: 'county', tracked: false },
  { id: 'C_CONDITIONS', code: 'CN3', label: 'Conditions', track: 'county', tracked: false },
  { id: 'C_CLOSED', code: 'CL2', label: 'County case closed', track: 'county', tracked: false },
  { id: 'C_TRIAL', code: 'JT2', label: 'County jury trial, if the offer is rejected', track: 'county', tracked: false },
];

export const CASE_JOURNEY: JourneyBranch[] = [
  { track: 'lor', title: 'Letter of representation', kind: 'phase', steps: LOR },
  { track: 'calendar', title: 'Calendar', kind: 'phase', steps: CALENDAR },
  { track: 'offers', title: 'Offer', kind: 'phase', steps: OFFERS },
  { track: 'closure', title: 'Closure', kind: 'phase', steps: CLOSURE },
  {
    track: 'appeal',
    title: 'If rejected — appeal',
    kind: 'outcome',
    note: 'Ends by opening a second case at county, which the system cannot do yet.',
    steps: APPEAL,
  },
  {
    track: 'trial',
    title: 'If rejected — jury trial',
    kind: 'outcome',
    note: 'Stays with the original court.',
    steps: TRIAL,
  },
  {
    track: 'county',
    title: 'The second case, at county',
    kind: 'outcome',
    note: 'A case carries one timeline today, so none of this can be recorded against it.',
    steps: COUNTY,
  },
];

/** The four rows every case has. */
export const JOURNEY_PHASES = CASE_JOURNEY.filter((b) => b.kind === 'phase');

/** The three paths a case may take after an offer, only one of which ever applies. */
export const JOURNEY_OUTCOMES = CASE_JOURNEY.filter((b) => b.kind === 'outcome');

export const JOURNEY_STEP_COUNT = CASE_JOURNEY.reduce((n, b) => n + b.steps.length, 0);

export const JOURNEY_TRACKED_COUNT = CASE_JOURNEY.reduce(
  (n, b) => n + b.steps.filter((s) => s.tracked).length,
  0,
);
